import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  FiCalendar,
  FiClock,
  FiShare2,
  FiCreditCard,
  FiFileText,
  FiRefreshCw,
  FiStar,
  FiCheck,
  FiArrowRight,
  FiChevronDown,
  FiGlobe,
  FiShield,
  FiCode,
  FiMail,
  FiPhone,
  FiMapPin,
  FiUser,
  FiExternalLink,
  FiCheckCircle,
  FiLock,
  FiRotateCcw,
  FiVideo,
} from "react-icons/fi";
import { HiOutlineSparkles } from "react-icons/hi2";
import { useLocale } from "../contexts/LocaleContext.jsx";
import { api } from "../lib/apiClient.js";

/**
 * DayCal public landing page (marketing). Design system: @daycal/ui tokens
 * (brand-*, featured, rounded-dc, shadow-dc) + Tailwind. Bilingual (EN/BN) via
 * the local COPY dictionary wired to LocaleContext — the toggle here also drives
 * the rest of the app's locale.
 */

const COPY = {
  en: {
    nav: {
      how: "How it works",
      features: "Features",
      pricing: "Pricing",
      faq: "FAQ",
      signin: "Sign in",
      start: "Get started",
    },
    badge: "Smart & Effortless Scheduling",
    heroLead: "The better way to",
    heroAccent: "schedule",
    heroTail: "your meetings",
    heroSub:
      "DayCal simplifies appointment scheduling. Share your custom booking link, sync calendars, collect payments, and automate form questions — seamlessly.",
    ctaFree: "Get started for free",
    ctaLive: "View live booking page",
    rating: "4.9/5",
    trust1: "Free forever for individuals",
    trust2: "No credit card required",
    howEyebrow: "How it works",
    howTitle: "Schedule in 3 simple steps",
    howSub: "Everything you need to automate your appointment booking flow.",
    featEyebrow: "Features",
    featTitle: "Everything you need to schedule bookings",
    featSub:
      "Packed with premium features built for individuals and developers.",
    priceEyebrow: "Flexible tiers",
    priceTitle: "Pick the plan that fits your schedule",
    priceSub:
      "Simple, transparent pricing for individuals, teams, and organizations.",
    faqEyebrow: "FAQ",
    faqTitle: "Frequently asked questions",
    ctaTitle: "Smarter, simpler scheduling",
    ctaSub: "Create your custom booking link in less than 2 minutes.",
    featuresList: "Features:",
    mostPopular: "Most popular",
    footerRights: "All rights reserved.",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    refund: "Refund Policy",
    contact: "Contact Us",
    footer: {
      tagline:
        "The modern, frictionless scheduling platform. Share your booking link, collect payments, and automate booking flows effortlessly.",
      status: "All Systems Operational • 99.9% Uptime",
      productTitle: "Product",
      howItWorks: "How it works",
      features: "Features & Tools",
      pricing: "Pricing Plans",
      faq: "FAQ & Help",
      signin: "Sign in to Account",
      start: "Get started for free",
      legalTitle: "Trust & Legal",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      refund: "Cancellation & Refund",
      contactTitle: "Official Contact",
      officer: "Dibyendu Kumar Khaskel",
      officerRole: "Grievance & Support Officer",
      email: "support@daycal.com",
      phone: "+91 6294629735",
      address: "Helencha Road, Duttapulia, Baikola, West Bengal 743270, India",
      hours: "Mon – Sat: 10 AM – 7 PM IST",
      rights: "All rights reserved.",
      builtWith: "Engineered with precision for global teams & creators.",
      language: "Language",
    },
    steps: [
      {
        title: "Create your events",
        body: "Set up your meeting types with duration, pricing, and location — built-in video call, phone, or in person.",
      },
      {
        title: "Set your availability",
        body: "Define your weekly working hours, breaks, and buffer times in your dashboard.",
      },
      {
        title: "Share your link",
        body: "Share your custom booking link `/u/username` and let clients schedule instantly.",
      },
    ],
    features: [
      {
        title: "Smart Weekly Schedules",
        body: "Configure flexible working hours and breaks for each day of the week.",
      },
      {
        title: "Free & Paid Bookings",
        body: "Collect payments upfront using secure Stripe or Razorpay integrations.",
      },
      {
        title: "Custom Booking Forms",
        body: "Build custom questions for your attendees with our form field builder.",
      },
      {
        title: "Built-in Video Calls",
        body: "Every online booking gets its own secure video room automatically — no Google Meet or Zoom account needed.",
      },
      {
        title: "Super Admin Panel",
        body: "Manage users, custom domains, plans, and global SMTP mail settings.",
      },
      {
        title: "REST API (v1)",
        body: "Secure bearer-token endpoints for mobile apps or external integrations.",
      },
    ],
    faqs: [
      {
        q: "Is DayCal completely free to use?",
        a: "Yes! DayCal MVP includes unlimited bookings, custom booking links, availability management, and custom form builders completely free.",
      },
      {
        q: "Do I need a Google Meet or Zoom account for video calls?",
        a: "No. Every online booking automatically gets its own secure DayCal video room — just share the link, with no external accounts or installs needed.",
      },
      {
        q: "Can I accept payments for appointments?",
        a: "Yes. You can mark any event as paid, set your price amount & currency, and integrate Stripe or Razorpay.",
      },
      {
        q: "Which languages does DayCal support?",
        a: "DayCal ships bilingual out of the box — English and Bengali — with a language switch available across the app and booking pages.",
      },
      {
        q: "Do I need to install any software to use DayCal?",
        a: "No, DayCal is completely web-based. You and your invitees can access it directly from any modern web browser without downloading anything.",
      },
      {
        q: "What happens if an invitee needs to cancel or reschedule?",
        a: "Invitees can easily cancel or reschedule through the link in their confirmation email. DayCal automatically updates your calendar and frees up the slot.",
      },
      {
        q: "Can I use DayCal with my team?",
        a: "Yes, you can upgrade to team-oriented plans and use the Super Admin panel to manage multiple users and appointments across your organization.",
      },
      {
        q: "Is it possible to customize the booking page?",
        a: "Absolutely! You can add custom questions using our form builder to collect all the necessary information from your invitees before the meeting.",
      },
      {
        q: "Are my payments secure?",
        a: "Yes, we integrate with industry leaders like Stripe and Razorpay to process payments securely. DayCal never stores your sensitive credit card information.",
      },
    ],
  },
  bn: {
    nav: {
      how: "যেভাবে কাজ করে",
      features: "ফিচার",
      pricing: "প্রাইসিং",
      faq: "প্রশ্নোত্তর",
      signin: "সাইন ইন",
      start: "শুরু করুন",
    },
    badge: "স্মার্ট ও সহজ শিডিউলিং",
    heroLead: "মিটিং শিডিউলের",
    heroAccent: "সহজ",
    heroTail: "উপায়",
    heroSub:
      "DayCal অ্যাপয়েন্টমেন্ট শিডিউলিং সহজ করে। কাস্টম বুকিং লিংক শেয়ার করুন, ক্যালেন্ডার সিংক করুন, পেমেন্ট নিন এবং ফর্ম প্রশ্ন স্বয়ংক্রিয় করুন।",
    ctaFree: "ফ্রি-তে শুরু করুন",
    ctaLive: "লাইভ বুকিং পেজ দেখুন",
    rating: "৪.৯/৫",
    trust1: "ব্যক্তিদের জন্য চিরকাল ফ্রি",
    trust2: "ক্রেডিট কার্ড লাগবে না",
    howEyebrow: "যেভাবে কাজ করে",
    howTitle: "৩টি সহজ ধাপে শিডিউল করুন",
    howSub:
      "আপনার অ্যাপয়েন্টমেন্ট বুকিং প্রবাহ স্বয়ংক্রিয় করতে যা প্রয়োজন সব।",
    featEyebrow: "ফিচার",
    featTitle: "বুকিং শিডিউলের জন্য প্রয়োজনীয় সবকিছু",
    featSub: "ব্যক্তি ও ডেভেলপারদের জন্য তৈরি প্রিমিয়াম ফিচারে সমৃদ্ধ।",
    priceEyebrow: "নমনীয় প্ল্যান",
    priceTitle: "আপনার শিডিউলের উপযুক্ত প্ল্যান বেছে নিন",
    priceSub: "ব্যক্তি, টিম ও প্রতিষ্ঠানের জন্য সহজ, স্বচ্ছ প্রাইসিং।",
    faqEyebrow: "প্রশ্নোত্তর",
    faqTitle: "সাধারণ জিজ্ঞাসা",
    ctaTitle: "স্মার্ট, সহজতর শিডিউলিং",
    ctaSub: "২ মিনিটেরও কম সময়ে আপনার কাস্টম বুকিং লিংক তৈরি করুন।",
    featuresList: "ফিচার:",
    mostPopular: "সর্বাধিক জনপ্রিয়",
    footerRights: "সর্বস্বত্ব সংরক্ষিত।",
    privacy: "প্রাইভেসি পলিসি",
    terms: "সেবার শর্তাবলী",
    refund: "রিফান্ড পলিসি",
    contact: "যোগাযোগ করুন",
    footer: {
      tagline:
        "আধুনিক এবং সহজ অ্যাপয়েন্টমেন্ট শিডিউলিং প্ল্যাটফর্ম। আপনার বুকিং লিংক শেয়ার করুন, পেমেন্ট নিন এবং বুকিং স্বয়ংক্রিয় করুন।",
      status: "সকল সিস্টেম সচল • ৯৯.৯% আপটাইম",
      productTitle: "প্রোডাক্ট",
      howItWorks: "যেভাবে কাজ করে",
      features: "ফিচার ও টুলস",
      pricing: "প্রাইসিং প্ল্যান",
      faq: "প্রশ্নোত্তর ও সাহায্য",
      signin: "অ্যাকাউন্টে সাইন ইন",
      start: "ফ্রি-তে শুরু করুন",
      legalTitle: "আইনি ও কমপ্লায়েন্স",
      privacy: "প্রাইভেসি পলিসি",
      terms: "সেবার শর্তাবলী",
      refund: "বাতিলকরণ ও রিফান্ড",
      contactTitle: "অফিসিয়াল যোগাযোগ",
      officer: "দিব্যেন্দু কুমার খাসকেল",
      officerRole: "অভিযোগ ও সাপোর্ট কর্মকর্তা",
      email: "support@daycal.com",
      phone: "+91 6294629735",
      address: "হেলেনচা রোড, দত্তপুলিয়া, বাইকোলা, পশ্চিমবঙ্গ ৭৪৩২৭০, ভারত",
      hours: "সোম – শনি: সকাল ১০:০০ – সন্ধ্যা ৭:০০",
      rights: "সর্বস্বত্ব সংরক্ষিত।",
      builtWith: "বিশ্বমানের টিম ও ক্রিয়েটরদের জন্য নির্মিত।",
      language: "ভাষা",
    },
    steps: [
      {
        title: "আপনার ইভেন্ট তৈরি করুন",
        body: "সময়কাল, মূল্য এবং লোকেশন সহ আপনার মিটিং টাইপ সেট করুন — বিল্ট-ইন ভিডিও কল, ফোন বা সরাসরি।",
      },
      {
        title: "আপনার প্রাপ্যতা সেট করুন",
        body: "আপনার ড্যাশবোর্ডে সাপ্তাহিক কাজের সময়, বিরতি এবং বাফার সময় নির্ধারণ করুন।",
      },
      {
        title: "আপনার লিংক শেয়ার করুন",
        body: "আপনার কাস্টম বুকিং লিংক `/u/username` শেয়ার করুন এবং ক্লায়েন্টদের তাৎক্ষণিকভাবে শিডিউল করতে দিন।",
      },
    ],
    features: [
      {
        title: "স্মার্ট সাপ্তাহিক শিডিউল",
        body: "সপ্তাহের প্রতিটি দিনের জন্য নমনীয় কাজের সময় এবং বিরতি কনফিগার করুন।",
      },
      {
        title: "ফ্রি এবং পেইড বুকিং",
        body: "নিরাপদ স্ট্রাইপ বা রেজারপে ইন্টিগ্রেশন ব্যবহার করে অগ্রিম পেমেন্ট সংগ্রহ করুন।",
      },
      {
        title: "কাস্টম বুকিং ফর্ম",
        body: "আমাদের ফর্ম ফিল্ড বিল্ডার দিয়ে অংশগ্রহণকারীদের জন্য কাস্টম প্রশ্ন তৈরি করুন।",
      },
      {
        title: "বিল্ট-ইন ভিডিও কল",
        body: "প্রতিটি অনলাইন বুকিং স্বয়ংক্রিয়ভাবে নিজস্ব নিরাপদ ভিডিও রুম পায় — গুগল মিট বা জুম অ্যাকাউন্টের প্রয়োজন নেই।",
      },
      {
        title: "সুপার অ্যাডমিন প্যানেল",
        body: "ইউজার, কাস্টম ডোমেইন, প্ল্যান এবং গ্লোবাল এসএমটিপি মেইল সেটিংস ম্যানেজ করুন।",
      },
      {
        title: "রেস্ট এপিআই (v1)",
        body: "মোবাইল অ্যাপ বা বাহ্যিক ইন্টিগ্রেশনের জন্য নিরাপদ বিয়ারার-টোকেন এন্ডপয়েন্ট।",
      },
    ],
    faqs: [
      {
        q: "DayCal কি ব্যবহার করার জন্য সম্পূর্ণ ফ্রি?",
        a: "হ্যাঁ! DayCal MVP-তে আনলিমিটেড বুকিং, কাস্টম বুকিং লিংক, প্রাপ্যতা ব্যবস্থাপনা এবং কাস্টম ফর্ম বিল্ডার সম্পূর্ণ ফ্রি।",
      },
      {
        q: "ভিডিও কলের জন্য কি আমার গুগল মিট বা জুম অ্যাকাউন্ট দরকার?",
        a: "না। প্রতিটি অনলাইন বুকিং স্বয়ংক্রিয়ভাবে নিজস্ব নিরাপদ DayCal ভিডিও রুম পায় — শুধু লিংক শেয়ার করুন, কোনো বাহ্যিক অ্যাকাউন্ট বা ইনস্টলের প্রয়োজন নেই।",
      },
      {
        q: "আমি কি অ্যাপয়েন্টমেন্টের জন্য পেমেন্ট নিতে পারি?",
        a: "হ্যাঁ। আপনি যেকোনো ইভেন্টকে পেইড হিসেবে মার্ক করতে পারেন, আপনার মূল্যের পরিমাণ এবং মুদ্রা সেট করতে পারেন এবং স্ট্রাইপ বা রেজারপে ইন্টিগ্রেট করতে পারেন।",
      },
      {
        q: "DayCal কোন ভাষা সমর্থন করে?",
        a: "DayCal ইংরেজি এবং বাংলা—বাইলিনগুয়াল সাপোর্ট সহ আসে, অ্যাপ এবং বুকিং পেজ জুড়ে ভাষা পরিবর্তন করার সুবিধা রয়েছে।",
      },
      {
        q: "DayCal ব্যবহার করার জন্য আমাকে কি কোনো সফটওয়্যার ইনস্টল করতে হবে?",
        a: "না, DayCal সম্পূর্ণ ওয়েব-ভিত্তিক। আপনি এবং আপনার আমন্ত্রিতরা কোনো কিছু ডাউনলোড না করেই যেকোনো আধুনিক ওয়েব ব্রাউজার থেকে সরাসরি এটি অ্যাক্সেস করতে পারবেন।",
      },
      {
        q: "কোনো আমন্ত্রিত ব্যক্তির যদি বাতিল বা পুনর্নির্ধারণ করার প্রয়োজন হয় তবে কী হবে?",
        a: "আমন্ত্রিতরা তাদের কনফার্মেশন ইমেলের লিঙ্কের মাধ্যমে সহজেই বাতিল বা পুনর্নির্ধারণ করতে পারেন। DayCal স্বয়ংক্রিয়ভাবে আপনার ক্যালেন্ডার আপডেট করে এবং স্লটটি খালি করে।",
      },
      {
        q: "আমি কি আমার টিমের সাথে DayCal ব্যবহার করতে পারি?",
        a: "হ্যাঁ, আপনি টিম-ভিত্তিক প্ল্যানগুলোতে আপগ্রেড করতে পারেন এবং আপনার প্রতিষ্ঠান জুড়ে একাধিক ব্যবহারকারী ও অ্যাপয়েন্টমেন্ট পরিচালনা করতে সুপার অ্যাডমিন প্যানেল ব্যবহার করতে পারেন।",
      },
      {
        q: "বুকিং পেজটি কাস্টমাইজ করা কি সম্ভব?",
        a: "অবশ্যই! মিটিংয়ের আগে আপনার আমন্ত্রিতদের কাছ থেকে প্রয়োজনীয় সমস্ত তথ্য সংগ্রহ করতে আপনি আমাদের ফর্ম বিল্ডার ব্যবহার করে কাস্টম প্রশ্ন যোগ করতে পারেন।",
      },
      {
        q: "আমার পেমেন্ট কি নিরাপদ?",
        a: "হ্যাঁ, নিরাপদে পেমেন্ট প্রক্রিয়া করার জন্য আমরা স্ট্রাইপ এবং রেজারপে-এর মতো শীর্ষস্থানীয় প্রতিষ্ঠানগুলোর সাথে যুক্ত। DayCal কখনোই আপনার সংবেদনশীল ক্রেডিট কার্ডের তথ্য সংরক্ষণ করে নিরাপদ রাখে।",
      },
    ],
  },
};

const STEP_ICONS = [FiRefreshCw, FiClock, FiShare2];

const FEATURE_STYLES = [
  { icon: FiCalendar, tint: "bg-indigo-50 text-indigo-500" },
  { icon: FiCreditCard, tint: "bg-emerald-50 text-emerald-500" },
  { icon: FiFileText, tint: "bg-amber-50 text-amber-500" },
  { icon: FiRefreshCw, tint: "bg-sky-50 text-sky-500" },
  { icon: FiShield, tint: "bg-fuchsia-50 text-fuchsia-500" },
  { icon: FiCode, tint: "bg-slate-100 text-slate-500" },
];

// Fallback pricing — only used if the /plans API is unreachable so the public
// page never renders empty. The live source of truth is the DB (Super Admin → Plans).
const FALLBACK_PLANS = [
  {
    id: "free",
    name: "Free",
    price: "₹0",
    per: "/ forever",
    tagline: "Everything you need for individual appointment scheduling.",
    features: [
      "1 user account & custom link (/u/username)",
      "1 event type maximum",
      "Weekly working hours & availability manager",
      "Custom booking form builder",
      "Bilingual support (English & Bengali)",
    ],
    cta: "Get Started",
    badge: "Free Forever",
    dark: false,
    highlight: false,
  },
  {
    id: "growth",
    name: "Growth",
    price: "₹749",
    per: "/ per month",
    tagline: "For active hosts needing built-in video calls and paid appointments.",
    features: [
      "Includes all Free features",
      "Built-in video calls for online bookings",
      "Accept paid bookings (Stripe & Razorpay)",
      "Custom booking form response logs",
      "Email & SMTP notification delivery",
    ],
    cta: "Upgrade",
    badge: "Popular",
    dark: false,
    highlight: false,
  },
  {
    id: "pro",
    name: "Pro",
    price: "₹1,499",
    per: "/ per month",
    tagline: "Full access to API endpoints and advanced scheduling controls.",
    features: [
      "Includes all Growth features",
      "REST API (v1) access with Bearer Token",
      "Mobile app integration support",
      "Priority appointment slot generator",
      "Detailed revenue & booking analytics",
    ],
    cta: "Upgrade",
    badge: "Most Popular",
    dark: true,
    highlight: true,
  },
  {
    id: "super",
    name: "Super Admin",
    price: "Custom",
    per: "/ platform license",
    tagline: "Complete system control and administration features.",
    features: [
      "Dedicated Super Admin Control Panel",
      "Global user account management & status controls",
      "Global appointments log across all hosts",
      "Custom site title & SMTP host configurations",
      "Full database & server control",
    ],
    cta: "Contact Admin",
    badge: "Full System Control",
    dark: true,
    highlight: false,
  },
];

// Map a DB plan row (Super Admin → Plans) into the pricing-card shape.
function mapPlan(p) {
  const badge = p.badge_tag || "";
  return {
    id: p.id,
    name: p.name,
    price: p.price_display,
    per: p.billing_cycle ? `/ ${p.billing_cycle}` : "",
    tagline: p.description || "",
    features: p.features_list || [],
    cta: p.button_text || "Get started",
    badge,
    dark: !!p.is_featured_dark,
    highlight: /most popular/i.test(badge),
  };
}

// FAQs removed, now in COPY.

function Logo() {
  return (
    <div className="group flex cursor-pointer items-center">
      <img
        src="/Day cal logo name.png"
        alt="DayCal Logo"
        className="h-16 w-auto -ml-3 -my-3 object-contain transition-transform duration-500 group-hover:scale-105"
      />
    </div>
  );
}

function LangToggle({ locale, setLocale }) {
  return (
    <div className="flex items-center rounded-full bg-slate-100 p-0.5 text-xs font-semibold">
      {[
        ["en", "ENG"],
        ["bn", "BN"],
      ].map(([code, label]) => (
        <button
          key={code}
          type="button"
          onClick={() => setLocale(code)}
          className={`rounded-full px-2.5 py-1 transition-colors ${
            locale === code
              ? "bg-slate-900 text-white shadow-sm"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function Eyebrow({ children }) {
  return (
    <p className="mb-3 text-center text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
      {children}
    </p>
  );
}

const SLOTS = ["09:00 AM", "11:30 AM", "02:00 PM"];

function BookingPreview() {
  const [targetSlot, setTargetSlot] = useState(0);
  const [activeSlot, setActiveSlot] = useState(0);
  const [isClicking, setIsClicking] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduce) return undefined;

    const cycleId = setInterval(() => {
      setTargetSlot((i) => (i + 1) % SLOTS.length);

      setTimeout(() => {
        setIsClicking(true);
      }, 500);

      setTimeout(() => {
        setActiveSlot((i) => (i + 1) % SLOTS.length);
        setIsClicking(false);
      }, 700);
    }, 2000);

    return () => clearInterval(cycleId);
  }, []);

  return (
    <div className="animate-dc-float rounded-dc border border-slate-400/80 bg-white p-5 shadow-[0_24px_60px_-24px_rgba(15,23,42,0.35)] sm:p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-dc-sm bg-slate-900 text-sm font-bold text-white">
            AU
          </span>
          <div>
            <p className="font-display text-sm font-bold text-slate-900">
              Admin User
            </p>
            <p className="text-xs text-slate-400">/u/admin</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-600">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active
        </span>
      </div>

      <div className="mt-5 rounded-dc-sm border border-slate-100 bg-surface p-4">
        <p className="font-display text-sm font-bold text-slate-900">
          30 Minute Strategy Session
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Select a date and available time slot to schedule our meeting.
        </p>
        <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1">
            <FiClock className="h-3.5 w-3.5" /> 30 Mins
          </span>
          <span className="inline-flex items-center gap-1">
            <FiGlobe className="h-3.5 w-3.5" /> Video Call
          </span>
          <span className="inline-flex items-center gap-1">
            <FiCreditCard className="h-3.5 w-3.5" /> Free
          </span>
        </div>
      </div>

      <p className="mt-5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        Available slots (Mon, Oct 24)
      </p>
      <div className="mt-2 relative">
        <div className="grid grid-cols-3 gap-2">
          {SLOTS.map((slot, i) => (
            <button
              key={slot}
              type="button"
              onClick={() => {
                setTargetSlot(i);
                setActiveSlot(i);
              }}
              aria-pressed={i === activeSlot}
              className={`relative z-10 rounded-dc-sm py-2.5 text-xs font-semibold transition-colors duration-300 ${
                i === activeSlot
                  ? "bg-slate-900 text-white"
                  : "bg-surface text-slate-600 hover:bg-slate-100"
              }`}
            >
              {slot}
            </button>
          ))}
        </div>

        {/* Animated Mouse Pointer */}
        <div
          className="absolute z-20 pointer-events-none"
          style={{
            top: "50%",
            left: `calc(${targetSlot * 33.33 + 16.66}% - 8px)`,
            transform: `scale(${isClicking ? 0.85 : 1})`,
            transformOrigin: "top left",
            transition: `left 500ms ease-in-out, transform ${isClicking ? "150ms" : "250ms"} ease-out`,
          }}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="black"
            stroke="white"
            strokeWidth="1.5"
            strokeLinejoin="round"
            style={{ filter: "drop-shadow(0px 4px 4px rgba(0,0,0,0.25))" }}
          >
            <path d="M5 3L19 12L13 14L16 21L13 22L10 15L5 20V3Z" />
          </svg>
        </div>
      </div>
    </div>
  );
}

function HowItWorksSteps({ steps }) {
  const [activeStep, setActiveStep] = useState(0);
  const [copiedLink, setCopiedLink] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduce || isPaused) return undefined;

    const cycleId = setInterval(() => {
      setActiveStep((i) => (i + 1) % steps.length);
    }, 3500);

    return () => clearInterval(cycleId);
  }, [steps.length, isPaused]);

  const handleCopy = () => {
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <div
      className="mt-14 space-y-10"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Step Progress Connector Bar (Desktop) */}
      <div className="hidden md:block relative max-w-3xl mx-auto px-12">
        <div className="h-1 w-full bg-slate-200/80 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-brand-600 via-indigo-600 to-emerald-500 transition-all duration-700 ease-out"
            style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Step Selection Cards */}
      <div className="grid gap-6 md:grid-cols-3">
        {steps.map((s, i) => {
          const isActive = i === activeStep;
          const Icon = STEP_ICONS[i] || FiClock;
          return (
            <button
              key={s.title}
              type="button"
              onClick={() => setActiveStep(i)}
              className={`text-left relative overflow-hidden rounded-dc border p-7 transition-all duration-500 ease-out cursor-pointer group ${
                isActive
                  ? "-translate-y-2 border-brand-500/60 bg-white shadow-xl shadow-brand-500/15 ring-2 ring-brand-500/30"
                  : "border-slate-300/80 bg-white/90 shadow-md shadow-slate-200/50 hover:border-slate-400 hover:bg-white hover:-translate-y-1"
              }`}
            >
              {/* Subtle top indicator beam */}
              <div
                className={`absolute top-0 left-0 right-0 h-1 transition-all duration-500 ${isActive ? "bg-brand-600" : "bg-transparent group-hover:bg-slate-200"}`}
              />

              <div
                className={`pointer-events-none absolute inset-0 bg-gradient-to-br from-brand-50/60 via-indigo-50/30 to-transparent transition-opacity duration-500 ${isActive ? "opacity-100" : "opacity-0"}`}
              />

              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span
                    className={`grid h-11 w-11 place-items-center rounded-dc-sm font-display text-lg font-bold transition-all duration-500 ${
                      isActive
                        ? "scale-110 bg-brand-600 text-white shadow-md shadow-brand-600/30"
                        : "bg-slate-900 text-white group-hover:scale-105"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <Icon
                    className={`h-5 w-5 transition-all duration-500 ${
                      isActive
                        ? "rotate-12 scale-125 text-brand-600"
                        : "text-slate-400 group-hover:text-slate-600"
                    }`}
                  />
                </div>
                {isActive && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-brand-700 bg-brand-100/80 px-2.5 py-0.5 rounded-full animate-pulse">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-600" />{" "}
                    Active
                  </span>
                )}
              </div>

              <h3
                className={`font-display relative mt-5 text-lg font-bold transition-colors duration-500 ${
                  isActive
                    ? "text-brand-800"
                    : "text-slate-900 group-hover:text-brand-600"
                }`}
              >
                {s.title}
              </h3>
              <p className="relative mt-2 text-sm leading-relaxed text-slate-500">
                {s.body}
              </p>
            </button>
          );
        })}
      </div>

      {/* Dynamic Interactive Demo Widget */}
      <div className="mx-auto max-w-2xl rounded-dc border border-slate-300/90 bg-white p-6 shadow-[0_20px_50px_-12px_rgba(15,23,42,0.22)] relative overflow-hidden transition-all duration-500">
        <div className="absolute -right-10 -bottom-10 h-32 w-32 rounded-full bg-brand-100/40 blur-2xl pointer-events-none" />

        <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-5">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full bg-rose-400" />
            <span className="h-3 w-3 rounded-full bg-amber-400" />
            <span className="h-3 w-3 rounded-full bg-emerald-400" />
            <span className="ml-2 text-xs font-mono text-slate-400 font-semibold">
              Live Preview — Step {activeStep + 1}
            </span>
          </div>
          <span className="text-xs text-brand-600 font-medium bg-brand-50 px-2 py-0.5 rounded-full border border-brand-100">
            Interactive
          </span>
        </div>

        {activeStep === 0 && (
          <div className="space-y-4 animate-dc-rise">
            <div className="flex items-center justify-between rounded-dc-sm bg-slate-900 p-4 border border-slate-800 text-white shadow-sm">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-dc-sm bg-brand-900/60 border border-brand-700/60 text-brand-400 shadow-sm">
                  <FiVideo className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-bold text-white">
                    30 Minute Strategy Session
                  </p>
                  <p className="text-[11px] text-slate-300">
                    Online · Built-in video call
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-950/90 px-3 py-1 text-xs font-semibold text-emerald-400 border border-emerald-500/30">
                <FiCheckCircle className="h-3.5 w-3.5 text-emerald-400" />{" "}
                Live
              </span>
            </div>
            <div className="rounded-dc-sm bg-slate-900 p-3.5 border border-slate-800 flex items-center justify-between text-xs text-slate-200 font-medium">
              <span>🎥 A secure video room is created automatically per booking</span>
              <span className="font-mono text-[10px] bg-brand-950 text-brand-300 border border-brand-800/80 px-2 py-0.5 rounded">
                No add-ons
              </span>
            </div>
          </div>
        )}

        {activeStep === 1 && (
          <div className="space-y-4 animate-dc-rise">
            <p className="text-xs font-semibold text-slate-500">
              Weekly Working Hours & Buffer Times
            </p>
            <div className="grid grid-cols-5 gap-2">
              {["Mon", "Tue", "Wed", "Thu", "Fri"].map((day, idx) => (
                <div
                  key={day}
                  className={`p-2.5 text-center rounded-dc-sm border text-xs font-medium transition-all ${
                    idx < 4
                      ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                      : "bg-slate-50 text-slate-400 border-slate-200"
                  }`}
                >
                  <p className="font-bold">{day}</p>
                  <p className="text-[10px] mt-0.5 opacity-80">
                    {idx < 4 ? "9am-5pm" : "Off"}
                  </p>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500 pt-1">
              <span>⏱️ 15-min buffer time added between meetings</span>
              <span className="text-emerald-600 font-semibold">
                Auto-calculated
              </span>
            </div>
          </div>
        )}

        {activeStep === 2 && (
          <div className="space-y-4 animate-dc-rise">
            <p className="text-xs font-semibold text-slate-500">
              Your Personal Booking Link
            </p>
            <div className="flex items-center gap-2 rounded-dc-sm bg-slate-900 p-2.5 text-white">
              <span className="text-xs font-mono flex-1 text-slate-300 truncate px-2">
                daycal.com/u/
                <span className="text-brand-400 font-bold">biswas-company</span>
              </span>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 rounded-dc-sm bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-brand-500 active:scale-95 shadow-sm"
              >
                {copiedLink ? (
                  <>
                    <FiCheckCircle className="h-3.5 w-3.5 text-emerald-300" />{" "}
                    Copied!
                  </>
                ) : (
                  <>
                    <FiShare2 className="h-3.5 w-3.5" /> Copy Link
                  </>
                )}
              </button>
            </div>
            <p className="text-[11px] text-slate-400 text-center">
              Share via email, bio link, or embed on your website
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const FEATURE_TAGS = [
  "Automated",
  "Stripe & Razorpay",
  "Custom Fields",
  "Built-in Video",
  "Super Admin",
  "v1 REST API",
];

function FeaturesAnimated({ features }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduce || isPaused) return undefined;

    const cycleId = setInterval(() => {
      setActiveIdx((i) => (i + 1) % features.length);
    }, 3000);

    return () => clearInterval(cycleId);
  }, [features.length, isPaused]);

  return (
    <div
      className="mt-14 space-y-10"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Feature Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {features.map((f, i) => {
          const isActive = i === activeIdx;
          const style = FEATURE_STYLES[i] || FEATURE_STYLES[0];
          const Icon = style.icon;
          const tag = FEATURE_TAGS[i] || "Feature";
          return (
            <button
              key={f.title}
              type="button"
              onClick={() => setActiveIdx(i)}
              className={`text-left group relative overflow-hidden rounded-dc border p-7 transition-all duration-500 ease-out cursor-pointer flex flex-col justify-between ${
                isActive
                  ? "-translate-y-2 border-brand-500/60 bg-white shadow-[0_20px_50px_-12px_rgba(15,23,42,0.18)] ring-2 ring-brand-500/30"
                  : "border-slate-300/80 bg-white/90 shadow-md shadow-slate-200/50 hover:border-slate-400 hover:bg-white hover:-translate-y-1"
              }`}
            >
              {/* Top ambient highlight line */}
              <div
                className={`absolute top-0 left-0 right-0 h-1 transition-all duration-500 ${isActive ? "bg-brand-600" : "bg-transparent group-hover:bg-slate-200"}`}
              />

              <div>
                <div className="flex items-center justify-between">
                  <span
                    className={`grid h-12 w-12 place-items-center rounded-dc-sm transition-all duration-500 ${style.tint} ${
                      isActive ? "scale-110 shadow-md" : "group-hover:scale-105"
                    }`}
                  >
                    <Icon
                      className={`h-6 w-6 transition-transform duration-500 ${isActive ? "scale-110 rotate-6" : ""}`}
                    />
                  </span>
                  <span
                    className={`text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full border transition-all ${
                      isActive
                        ? "bg-brand-100 text-brand-700 border-brand-200"
                        : "bg-slate-100 text-slate-500 border-slate-200 group-hover:bg-slate-200"
                    }`}
                  >
                    {tag}
                  </span>
                </div>

                <h3
                  className={`font-display text-lg font-bold mt-5 transition-colors duration-500 ${
                    isActive
                      ? "text-brand-900"
                      : "text-slate-900 group-hover:text-brand-600"
                  }`}
                >
                  {f.title}
                </h3>

                <p className="mt-2 text-sm leading-relaxed text-slate-500">
                  {f.body}
                </p>
              </div>

              <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-3 text-xs font-semibold">
                <span
                  className={isActive ? "text-brand-600" : "text-slate-400"}
                >
                  {isActive ? "● Live Spotlight Active" : "Click to explore"}
                </span>
                <FiArrowRight
                  className={`h-4 w-4 transition-transform duration-300 ${isActive ? "translate-x-1 text-brand-600" : "text-slate-300 group-hover:translate-x-1 group-hover:text-slate-600"}`}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* Feature Live Spotlight Card */}
      <div className="mx-auto max-w-3xl rounded-dc border border-slate-800 bg-slate-950 p-6 text-white shadow-[0_20px_50px_-12px_rgba(15,23,42,0.35)] relative overflow-hidden">
        <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-brand-600/20 blur-3xl pointer-events-none" />

        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-800 pb-4 mb-4">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-dc-sm bg-brand-600 text-white font-bold shadow-sm">
              #{activeIdx + 1}
            </span>
            <div>
              <p className="text-xs font-bold text-slate-200 uppercase tracking-wider">
                Spotlight Feature
              </p>
              <p className="text-sm font-bold text-white">
                {features[activeIdx]?.title}
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-950 px-3 py-1 text-xs font-semibold text-brand-300 border border-brand-800">
            <span className="h-2 w-2 rounded-full bg-brand-400 animate-ping" />{" "}
            Built for Scale
          </span>
        </div>

        <div className="grid gap-4 sm:grid-cols-3 pt-1 text-xs">
          <div className="p-3 rounded-dc-sm bg-slate-900 border border-slate-800">
            <p className="text-slate-400 text-[10px] uppercase font-bold">
              Integration
            </p>
            <p className="text-slate-200 font-semibold mt-0.5">
              Seamless & Fast
            </p>
          </div>
          <div className="p-3 rounded-dc-sm bg-slate-900 border border-slate-800">
            <p className="text-slate-400 text-[10px] uppercase font-bold">
              Availability
            </p>
            <p className="text-slate-200 font-semibold mt-0.5">
              24/7 Automation
            </p>
          </div>
          <div className="p-3 rounded-dc-sm bg-slate-900 border border-slate-800">
            <p className="text-slate-400 text-[10px] uppercase font-bold">
              Security
            </p>
            <p className="text-emerald-400 font-semibold mt-0.5">
              OAuth 2.0 Encrypted
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

import { SEO } from "@daycal/ui";

export default function Landing() {
  const { locale, setLocale } = useLocale();
  const t = COPY[locale] || COPY.en;
  const [openFaq, setOpenFaq] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Live pricing from Super Admin → Plans (shared ['plans'] query key). Falls back
  // to a built-in list only if the public API is unreachable.
  const { data: plansData } = useQuery({
    queryKey: ["plans"],
    queryFn: api.listPlans,
  });
  const plans = plansData?.plans?.length
    ? plansData.plans.map(mapPlan)
    : FALLBACK_PLANS;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO path="/" />
      {/* Nav */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "pt-4 px-4" : "pt-0"}`}
      >
        <div
          className={`mx-auto flex items-center justify-between transition-all duration-300 ${
            isScrolled
              ? "max-w-4xl rounded-full bg-white/95 backdrop-blur-lg shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-200/50 px-6 py-2.5"
              : "max-w-6xl bg-white/80 backdrop-blur-md px-5 py-3.5 border-b border-slate-100/0"
          }`}
        >
          <Logo />
          <nav className="hidden items-center gap-8 text-sm font-medium text-slate-600 md:flex">
            <a href="#how" className="hover:text-slate-900">
              {t.nav.how}
            </a>
            <a href="#features" className="hover:text-slate-900">
              {t.nav.features}
            </a>
            <a href="#pricing" className="hover:text-slate-900">
              {t.nav.pricing}
            </a>
            <a href="#faq" className="hover:text-slate-900">
              {t.nav.faq}
            </a>
          </nav>
          <div className="flex items-center gap-3">
            <LangToggle locale={locale} setLocale={setLocale} />
            <Link
              to="/login"
              className="hidden text-sm font-medium text-slate-600 hover:text-slate-900 sm:block"
            >
              {t.nav.signin}
            </Link>
            <Link
              to="/signup"
              className="hidden sm:inline-block rounded-dc-sm bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
            >
              {t.nav.start}
            </Link>
            <Link
              to="/login"
              className="sm:hidden rounded-dc-sm bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
            >
              {t.nav.signin}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-dot-grid" />
        <div className="pointer-events-none absolute -left-24 top-10 h-[30rem] w-[30rem] animate-pulse rounded-full bg-indigo-200/40 blur-[100px] [animation-duration:8s]" />
        <div className="pointer-events-none absolute right-0 top-40 h-[30rem] w-[30rem] animate-pulse rounded-full bg-emerald-200/30 blur-[100px] [animation-duration:11s]" />

        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-5 pt-28 pb-16 lg:grid-cols-2 lg:pt-36 lg:pb-24">
          <div className="animate-dc-rise">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-400 bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
              <HiOutlineSparkles className="h-3.5 w-3.5 text-brand-600" />{" "}
              {t.badge}
            </span>
            <h1 className="font-display mt-6 text-5xl font-extrabold leading-[1.05] text-slate-900 sm:text-6xl">
              {t.heroLead}{" "}
              <span className="relative whitespace-nowrap text-brand-600">
                {t.heroAccent}
                <svg
                  className="absolute bottom-0.5 left-0 w-full"
                  height="10"
                  viewBox="0 0 200 10"
                  fill="none"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M2 7C50 2 150 2 198 7"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    className="text-brand-500"
                  />
                </svg>
              </span>{" "}
              {t.heroTail}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-500">
              {t.heroSub}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 rounded-dc-sm bg-slate-900 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-slate-900/10 transition-transform hover:-translate-y-0.5 hover:bg-slate-800"
              >
                {t.ctaFree} <FiArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="#how"
                className="inline-flex items-center gap-2 rounded-dc-sm border border-slate-400 bg-white px-6 py-3.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-surface"
              >
                {t.ctaLive}
              </a>
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-slate-100 pt-6 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="flex text-amber-400">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <FiStar key={i} className="h-4 w-4 fill-current" />
                  ))}
                </span>
                <span className="font-semibold text-slate-700">{t.rating}</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <FiCheck className="h-4 w-4 text-emerald-500" /> {t.trust1}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <FiCheck className="h-4 w-4 text-emerald-500" /> {t.trust2}
              </span>
            </div>
          </div>

          <div className="animate-dc-rise [animation-delay:120ms]">
            <BookingPreview />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section
        id="how"
        className="relative overflow-hidden border-t border-slate-100 bg-gradient-to-b from-slate-50/50 via-white to-slate-50/50 py-24"
      >
        {/* Ambient background glow spots */}
        <div className="pointer-events-none absolute -left-20 top-1/2 -translate-y-1/2 h-80 w-80 rounded-full bg-brand-200/30 blur-[90px]" />
        <div className="pointer-events-none absolute -right-20 top-1/3 h-80 w-80 rounded-full bg-indigo-200/30 blur-[90px]" />

        <div className="relative mx-auto max-w-6xl px-5">
          <Eyebrow>{t.howEyebrow}</Eyebrow>
          <h2 className="font-display text-center text-4xl font-extrabold text-slate-900 sm:text-5xl">
            {t.howTitle}
          </h2>
          <p className="mt-3.5 text-center text-base sm:text-lg text-slate-500 max-w-xl mx-auto leading-relaxed">
            {t.howSub}
          </p>

          <HowItWorksSteps steps={t.steps} />
        </div>
      </section>

      {/* Features */}
      <section
        id="features"
        className="relative overflow-hidden py-24 bg-white"
      >
        <div className="pointer-events-none absolute right-0 top-1/4 h-96 w-96 rounded-full bg-brand-100/30 blur-[100px]" />
        <div className="pointer-events-none absolute -left-20 bottom-10 h-80 w-80 rounded-full bg-emerald-100/30 blur-[100px]" />

        <div className="relative mx-auto max-w-6xl px-5">
          <Eyebrow>{t.featEyebrow}</Eyebrow>
          <h2 className="font-display mx-auto max-w-3xl text-center text-4xl font-extrabold text-slate-900 sm:text-5xl">
            {t.featTitle}
          </h2>
          <p className="mt-3.5 text-center text-base sm:text-lg text-slate-500 max-w-xl mx-auto leading-relaxed">
            {t.featSub}
          </p>

          <FeaturesAnimated features={t.features} />
        </div>
      </section>

      {/* Pricing */}
      <section
        id="pricing"
        className="border-t border-slate-100 bg-surface py-20"
      >
        <div className="mx-auto max-w-6xl px-5">
          <Eyebrow>{t.priceEyebrow}</Eyebrow>
          <h2 className="font-display text-center text-4xl font-bold text-slate-900">
            {t.priceTitle}
          </h2>
          <p className="mt-3 text-center text-slate-500">{t.priceSub}</p>

          <div className="mt-14 grid gap-5 lg:grid-cols-4">
            {plans.map((p) => (
              <div
                key={p.name}
                className={`relative flex flex-col rounded-dc border p-6 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${
                  p.dark
                    ? "border-slate-800 bg-featured text-featured-text shadow-xl shadow-brand-900/30 hover:border-slate-600 hover:shadow-brand-900/40"
                    : "border-slate-400/70 bg-white shadow-sm hover:border-brand-500/40"
                } ${p.highlight ? "z-10 lg:-translate-y-4 lg:scale-[1.03] hover:lg:-translate-y-6" : ""}`}
              >
                {p.badge && (
                  <span
                    className={`absolute -top-3 right-5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${
                      p.highlight
                        ? "bg-brand-600 text-white"
                        : p.dark
                          ? "bg-brand-500 text-white"
                          : "bg-slate-900 text-white"
                    }`}
                  >
                    {p.highlight ? t.mostPopular : p.badge}
                  </span>
                )}
                <h3
                  className={`font-display text-lg font-bold ${p.dark ? "text-white" : "text-slate-900"}`}
                >
                  {p.name}
                </h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span
                    className={`font-display text-4xl font-extrabold ${p.dark ? "text-white" : "text-slate-900"}`}
                  >
                    {p.price}
                  </span>
                  <span
                    className={`text-sm ${p.dark ? "text-slate-400" : "text-slate-400"}`}
                  >
                    {p.per}
                  </span>
                </div>
                <p
                  className={`mt-3 text-sm leading-relaxed ${p.dark ? "text-slate-300" : "text-slate-500"}`}
                >
                  {p.tagline}
                </p>

                <div
                  className={`my-6 h-px ${p.dark ? "bg-slate-700" : "bg-slate-100"}`}
                />
                <p
                  className={`text-[11px] font-bold uppercase tracking-wider ${p.dark ? "text-slate-400" : "text-slate-400"}`}
                >
                  {t.featuresList}
                </p>
                <ul className="mt-4 flex-1 space-y-3">
                  {p.features.map((feat) => (
                    <li
                      key={feat}
                      className={`flex items-start gap-2.5 text-sm ${p.dark ? "text-slate-200" : "text-slate-600"}`}
                    >
                      <FiCheck
                        className={`mt-0.5 h-4 w-4 shrink-0 ${p.dark ? "text-brand-500" : "text-emerald-500"}`}
                      />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/signup"
                  className={`mt-7 inline-flex items-center justify-center gap-2 rounded-dc-sm px-4 py-3 text-sm font-semibold transition-colors ${
                    p.dark
                      ? "bg-white text-slate-900 hover:bg-slate-100"
                      : "bg-slate-900 text-white hover:bg-slate-800"
                  }`}
                >
                  {p.cta} <FiArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20">
        <div className="mx-auto max-w-3xl px-5">
          <Eyebrow>{t.faqEyebrow}</Eyebrow>
          <h2 className="font-display text-center text-4xl font-bold text-slate-900">
            {t.faqTitle}
          </h2>

          <div className="mt-12 space-y-3">
            {t.faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <div
                  key={f.q}
                  className={`group overflow-hidden rounded-dc border transition-all duration-300 ${open ? "border-brand-500/40 bg-brand-50/30 shadow-md shadow-brand-500/5" : "border-slate-400/70 bg-white hover:border-slate-400"}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left outline-none"
                    aria-expanded={open}
                  >
                    <span
                      className={`font-display text-base font-bold transition-colors ${open ? "text-brand-700" : "text-slate-900 group-hover:text-brand-600"}`}
                    >
                      {f.q}
                    </span>
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all duration-500 ${open ? "rotate-180 bg-brand-100 text-brand-600" : "bg-slate-100 text-slate-400 group-hover:bg-slate-200 group-hover:text-slate-600"}`}
                    >
                      <FiChevronDown className="h-5 w-5" />
                    </span>
                  </button>
                  <div
                    className={`grid transition-all duration-300 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                  >
                    <div className="overflow-hidden">
                      <p className="px-6 pb-5 text-sm leading-relaxed text-slate-500">
                        {f.a}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden border-t border-slate-100 bg-white py-24">
        {/* Background dotted grid and blur blobs */}
        <div className="pointer-events-none absolute inset-0 bg-dot-grid opacity-50" />
        <div className="pointer-events-none absolute -left-20 top-0 h-[20rem] w-[20rem] rounded-full bg-brand-200/30 blur-[80px] animate-dc-pulse-glow" />
        <div className="pointer-events-none absolute -right-20 bottom-0 h-[20rem] w-[20rem] rounded-full bg-emerald-200/20 blur-[80px] animate-dc-pulse-glow [animation-delay:1.5s]" />

        {/* Floating background icons */}
        <div className="absolute top-12 left-12 md:left-32 animate-dc-float opacity-25 [animation-delay:0s] pointer-events-none">
          <FiCalendar className="h-10 w-10 text-brand-500" />
        </div>
        <div className="absolute bottom-12 left-16 md:left-40 animate-dc-float opacity-20 [animation-delay:2s] pointer-events-none">
          <FiClock className="h-8 w-8 text-slate-400" />
        </div>
        <div className="absolute top-14 right-12 md:right-32 animate-dc-float opacity-25 [animation-delay:1s] pointer-events-none">
          <FiGlobe className="h-9 w-9 text-brand-400" />
        </div>
        <div className="absolute bottom-16 right-16 md:right-40 animate-dc-float opacity-20 [animation-delay:3s] pointer-events-none">
          <FiStar className="h-8 w-8 text-amber-400" />
        </div>

        <div className="relative z-10 mx-auto max-w-2xl px-5 text-center">
          <h2 className="font-display text-4xl font-extrabold text-slate-900 sm:text-5xl">
            {t.ctaTitle}
          </h2>
          <p className="mt-4 text-lg text-slate-500">{t.ctaSub}</p>
          <Link
            to="/signup"
            className="mt-8 inline-flex items-center gap-2 rounded-dc-sm bg-slate-900 px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-slate-900/10 transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.02] hover:bg-slate-800 active:scale-[0.98] hover:shadow-xl hover:shadow-slate-900/20"
          >
            {t.ctaFree} <FiArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Redesigned Modern Footer */}
      <footer className="border-t border-slate-800 bg-slate-950 text-slate-400">
        {/* Main Footer Content */}
        <div className="mx-auto max-w-6xl px-5 pt-16 pb-12">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-12">
            {/* Brand & Identity Column (5 cols) */}
            <div className="lg:col-span-4 space-y-4">
              <div className="flex items-center gap-3">
                <img src="/Day cal logo.png" alt="DayCal Logo" className="h-10 w-auto object-contain rounded-full" />
                <span className="font-display text-2xl font-bold text-white tracking-tight">
                  DayCal
                </span>
              </div>

              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed max-w-sm">
                {t.footer.tagline}
              </p>

              {/* Status Badge */}
              <div className="pt-1">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-950/50 px-3 py-1.5 text-xs font-semibold text-emerald-400 shadow-sm">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  <span>{t.footer.status}</span>
                </div>
              </div>
            </div>

            {/* Product Links (2 cols) */}
            <div className="lg:col-span-2 space-y-4">
              <h4 className="font-display text-xs font-bold uppercase tracking-wider text-slate-200">
                {t.footer.productTitle}
              </h4>
              <ul className="space-y-2.5 text-xs sm:text-sm">
                <li>
                  <a
                    href="#how"
                    className="hover:text-white transition-colors block"
                  >
                    {t.footer.howItWorks}
                  </a>
                </li>
                <li>
                  <a
                    href="#features"
                    className="hover:text-white transition-colors block"
                  >
                    {t.footer.features}
                  </a>
                </li>
                <li>
                  <a
                    href="#pricing"
                    className="hover:text-white transition-colors block"
                  >
                    {t.footer.pricing}
                  </a>
                </li>
                <li>
                  <a
                    href="#faq"
                    className="hover:text-white transition-colors block"
                  >
                    {t.footer.faq}
                  </a>
                </li>
                <li>
                  <Link
                    to="/login"
                    className="hover:text-white transition-colors block"
                  >
                    {t.footer.signin}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/signup"
                    className="text-brand-400 hover:text-brand-300 font-medium transition-colors block"
                  >
                    {t.footer.start} &rarr;
                  </Link>
                </li>
              </ul>
            </div>

            {/* Trust & Legal (3 cols) */}
            <div className="lg:col-span-3 space-y-4">
              <h4 className="font-display text-xs font-bold uppercase tracking-wider text-slate-200">
                {t.footer.legalTitle}
              </h4>
              <ul className="space-y-2.5 text-xs sm:text-sm">
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-white transition-colors block"
                  >
                    {t.footer.privacy}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-white transition-colors block"
                  >
                    {t.footer.terms}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/refund"
                    className="hover:text-white transition-colors block"
                  >
                    {t.footer.refund}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="hover:text-white transition-colors block"
                  >
                    {t.contact}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Official Point of Contact & Grievance (3 cols) */}
            <div className="lg:col-span-3 space-y-4">
              <h4 className="font-display text-xs font-bold uppercase tracking-wider text-slate-200">
                {t.footer.contactTitle}
              </h4>
              <div className="space-y-3 text-xs">
                <div className="flex items-start gap-2.5">
                  <FiUser className="h-4 w-4 text-brand-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-200">
                      {t.footer.officer}
                    </p>
                    <p className="text-slate-500 text-[11px]">
                      {t.footer.officerRole}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2.5">
                  <FiMail className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                  <div>
                    <a
                      href={`mailto:${t.footer.email}`}
                      className="text-slate-300 hover:text-white transition-colors font-medium"
                    >
                      {t.footer.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-2.5">
                  <FiPhone className="h-4 w-4 text-purple-400 mt-0.5 shrink-0" />
                  <div>
                    <a
                      href={`tel:${t.footer.phone.replace(/\s+/g, "")}`}
                      className="text-slate-300 hover:text-white transition-colors font-medium"
                    >
                      {t.footer.phone}
                    </a>
                    <p className="text-slate-500 text-[11px] mt-0.5">
                      {t.footer.hours}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2.5">
                  <FiMapPin className="h-4 w-4 text-sky-400 mt-0.5 shrink-0" />
                  <p className="text-slate-400 text-[11px] leading-relaxed">
                    {t.footer.address}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sub-Footer Bottom Bar */}
        <div className="border-t border-slate-900 bg-slate-950/80 py-6">
          <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 text-xs text-slate-500 sm:flex-row">
            <p>
              © {new Date().getFullYear()} DayCal Platform Inc.{" "}
              {t.footer.rights}
            </p>

            <p className="hidden md:block text-slate-500 text-[11px]">
              {t.footer.builtWith}
            </p>

            <div className="flex items-center gap-3">
              <span className="text-slate-500 text-xs font-medium">
                {t.footer.language}:
              </span>
              <LangToggle locale={locale} setLocale={setLocale} />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
